import { type JSX, useCallback, useEffect, useState } from 'react';

import {
  alsBase64,
  laeufe as ladeLaeufe,
  trainerUebernehmen,
  type Laufzeile,
  type Uebernahmebericht,
} from '../api/uebernahme';
import { ApiFehler } from '../api/fehler';
import { alsZeitpunkt } from '../datum';
import { Feldraster } from '../komponenten/Akte';
import { Einstellungsseite, Thema } from '../komponenten/Einstellungen';
import { Meldung } from '../komponenten/Meldung';

/**
 * Die Datenübernahme (Kapitel 29) — heute die Trainer.
 *
 * ============================================================================
 * ERST DER PROBELAUF, DANN DER ZWEITE KLICK (MIG-10)
 * ============================================================================
 *
 * Die Maske hat zwei Knöpfe, und der zweite ist stumm, bis der erste
 * gelaufen ist: „Probelauf" tut alles und rollt zurück; „Übernehmen" gibt es
 * erst, wenn derselbe Stand ohne Beanstandung durchgelaufen ist. Wer eine
 * Datei ändert, beginnt von vorn — der Probelauf gilt für die Bytes, die er
 * gesehen hat, nicht für den Dateinamen.
 *
 * ============================================================================
 * DER BERICHT SAGT ZAHLEN UND FUNDSTELLEN
 * ============================================================================
 *
 * So viele Zeilen, so viele neu, so viele fortgeschrieben — und jede
 * Beanstandung mit Datei und Zeilennummer, damit sie in Excel wiederzufinden
 * ist. Eine Beanstandung weist die ganze Datei ab (Festlegung des
 * Auftraggebers); „15 von 42 übernommen" gibt es nicht, weil niemand sähe,
 * welche 27 fehlen.
 *
 * ============================================================================
 * DAS GEDÄCHTNIS STEHT DARUNTER
 * ============================================================================
 *
 * Jeder Lauf — auch jeder Probelauf — steht in der Liste: wann, wer, welche
 * Datei, was herauskam. Das ist der Nachweis, den MIG-10 verlangt, und die
 * Antwort auf die Frage „haben wir die Datei vom Dienstag schon eingelesen?".
 *
 * SEITENTYP EINSTELLUNGEN (ÄA-41): Einlesen und Bericht stehen als zwei
 * Karten nebeneinander — wer den Probelauf startet, sieht sein Ergebnis,
 * ohne zu rollen. Die Läufe nehmen als breite Tabelle die ganze Reihe.
 */
export function Uebernahme({
  darfUebernehmen,
}: {
  readonly darfUebernehmen: boolean;
}): JSX.Element {
  const [trainer, setzeTrainer] = useState<File | null>(null);
  const [qualifikationen, setzeQualifikationen] = useState<File | null>(null);
  const [bericht, setzeBericht] = useState<Uebernahmebericht | null>(null);
  /** Der Stand, den der letzte saubere Probelauf gesehen hat — Name und Größe beider Dateien. */
  const [geprueft, setzeGeprueft] = useState<string | null>(null);
  const [laeuft, setzeLaeuft] = useState(false);
  const [laeufe, setzeLaeufe] = useState<readonly Laufzeile[]>([]);
  const [fehler, setzeFehler] = useState<ApiFehler | null>(null);

  const holen = useCallback(async () => {
    try {
      setzeLaeufe(await ladeLaeufe());
    } catch (f) {
      setzeFehler(f instanceof ApiFehler ? f : null);
      if (!(f instanceof ApiFehler)) throw f;
    }
  }, []);

  useEffect(() => {
    void holen();
  }, [holen]);

  const stand = (): string | null =>
    trainer === null || qualifikationen === null
      ? null
      : `${trainer.name}:${String(trainer.size)}|${qualifikationen.name}:${String(qualifikationen.size)}`;

  async function lauf(probelauf: boolean): Promise<void> {
    if (trainer === null || qualifikationen === null) return;
    setzeLaeuft(true);
    setzeFehler(null);
    try {
      const ergebnis = await trainerUebernehmen({
        trainerBase64: await alsBase64(trainer),
        qualifikationenBase64: await alsBase64(qualifikationen),
        dateiname: `${trainer.name} + ${qualifikationen.name}`,
        probelauf,
      });
      setzeBericht(ergebnis);
      setzeGeprueft(probelauf && ergebnis.beanstandungen.length === 0 ? stand() : null);
      await holen();
    } catch (f) {
      setzeFehler(f instanceof ApiFehler ? f : null);
      if (!(f instanceof ApiFehler)) throw f;
    } finally {
      setzeLaeuft(false);
    }
  }

  const uebernehmbar = darfUebernehmen && geprueft !== null && geprueft === stand() && !laeuft;

  return (
    <div className="stapel">
      <p className="hilfe">
        Zwei Dateien, wie sie unter <code>docs/stammdaten</code> beschrieben sind: eine Zeile je
        Trainer und eine Zeile je Qualifikation. Der Probelauf prüft alles und schreibt nichts;
        übernommen wird erst mit dem zweiten Klick — und nur, wenn keine Zeile beanstandet ist.
      </p>

      {fehler !== null && <Meldung fehler={fehler} />}

      <Einstellungsseite>
        <Thema titel="Trainer einlesen">
          <Feldraster>
            <div className="feld">
              <label className="knopf knopf--klein">
                trainer.csv wählen
                <input
                  type="file"
                  accept=".csv,text/csv"
                  className="nur-vorlesen"
                  aria-label="Datei trainer.csv"
                  disabled={!darfUebernehmen}
                  onChange={(e) => {
                    setzeTrainer(e.target.files?.[0] ?? null);
                    setzeGeprueft(null);
                  }}
                />
              </label>{' '}
              {trainer !== null && <span className="hilfe">{trainer.name}</span>}
            </div>
            <div className="feld">
              <label className="knopf knopf--klein">
                trainer-qualifikationen.csv wählen
                <input
                  type="file"
                  accept=".csv,text/csv"
                  className="nur-vorlesen"
                  aria-label="Datei trainer-qualifikationen.csv"
                  disabled={!darfUebernehmen}
                  onChange={(e) => {
                    setzeQualifikationen(e.target.files?.[0] ?? null);
                    setzeGeprueft(null);
                  }}
                />
              </label>{' '}
              {qualifikationen !== null && <span className="hilfe">{qualifikationen.name}</span>}
            </div>
          </Feldraster>
          <div className="knopfreihe">
            <button
              type="button"
              className="knopf"
              disabled={!darfUebernehmen || trainer === null || qualifikationen === null || laeuft}
              onClick={() => void lauf(true)}
            >
              {laeuft ? 'Läuft …' : 'Probelauf'}
            </button>
            {/*
              DER ZWEITE KNOPF IST STUMM, BIS DER ERSTE SAUBER WAR — und für
              genau diese Dateien. Eine andere Datei, ein neuer Probelauf.
            */}
            <button
              type="button"
              className="knopf knopf--haupt"
              disabled={!uebernehmbar}
              onClick={() => void lauf(false)}
            >
              Übernehmen
            </button>
          </div>
        </Thema>

        {bericht !== null && (
          <Thema titel={bericht.probelauf ? 'Probelauf' : 'Übernahme'}>
            {/* Die Karte ist eine Rückmeldung: Vorlesehilfen hören Ergebnis und Zahlen. */}
            <div role="status">
              <p>
                {bericht.uebernommen ? (
                  <span className="marke marke--gut">übernommen</span>
                ) : bericht.beanstandungen.length > 0 ? (
                  <span className="marke marke--krit">abgewiesen</span>
                ) : (
                  <span className="marke">nichts geschrieben</span>
                )}
              </p>
              <dl className="angaben">
                <dt>Trainer in der Datei</dt>
                <dd className="zahl">{bericht.zeilen}</dd>
                <dt>Qualifikationszeilen</dt>
                <dd className="zahl">{bericht.weitere['qualifikationen'] ?? 0}</dd>
                <dt>{bericht.probelauf ? 'Würden angelegt' : 'Angelegt'}</dt>
                <dd className="zahl">{bericht.angelegt}</dd>
                <dt>{bericht.probelauf ? 'Würden fortgeschrieben' : 'Fortgeschrieben'}</dt>
                <dd className="zahl">{bericht.fortgeschrieben}</dd>
                <dt>Beanstandet</dt>
                <dd className="zahl">{bericht.beanstandungen.length}</dd>
              </dl>
              {bericht.beanstandungen.length > 0 && (
                <>
                  <p>
                    <strong>
                      Die Datei wurde nicht übernommen — eine beanstandete Zeile weist sie ganz ab.
                    </strong>
                  </p>
                  <div className="tabellenhuelle tabellenhuelle--telefon">
                    <table className="liste__tabelle" aria-label="Beanstandungen">
                      <thead>
                        <tr>
                          <th scope="col">Datei</th>
                          <th scope="col">Zeile</th>
                          <th scope="col">Kennung</th>
                          <th scope="col">Grund</th>
                        </tr>
                      </thead>
                      <tbody>
                        {bericht.beanstandungen.map((b, i) => (
                          <tr key={`${b.datei}-${String(b.zeile)}-${String(i)}`}>
                            <td>{b.datei}</td>
                            <td className="zahl">{b.zeile}</td>
                            <td>{b.altkennung ?? '—'}</td>
                            <td>{b.grund}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
          </Thema>
        )}

        <Thema titel="Bisherige Läufe" breit>
          {laeufe.length === 0 ? (
            <p className="hilfe">Noch kein Lauf.</p>
          ) : (
            <div className="tabellenhuelle tabellenhuelle--telefon">
              <table className="liste__tabelle" aria-label="Bisherige Läufe">
                <thead>
                  <tr>
                    <th scope="col">Wann</th>
                    <th scope="col">Wer</th>
                    <th scope="col">Bereich</th>
                    <th scope="col">Datei</th>
                    <th scope="col">Art</th>
                    <th scope="col">Zeilen</th>
                    <th scope="col">Neu</th>
                    <th scope="col">Fortgeschrieben</th>
                    <th scope="col">Beanstandet</th>
                  </tr>
                </thead>
                <tbody>
                  {laeufe.map((l) => (
                    <tr key={l.id}>
                      <td className="liste__einzeilig">{alsZeitpunkt(l.angelegtAm)}</td>
                      <td>{l.angelegtVon ?? '—'}</td>
                      <td>{l.bereich}</td>
                      <td>{l.dateiname ?? '—'}</td>
                      <td>
                        {l.uebernommen ? (
                          <span className="marke marke--gut">übernommen</span>
                        ) : l.probelauf ? (
                          <span className="marke">Probelauf</span>
                        ) : (
                          <span className="marke marke--krit">abgewiesen</span>
                        )}
                      </td>
                      <td className="zahl">{l.zeilen}</td>
                      <td className="zahl">{l.angelegt}</td>
                      <td className="zahl">{l.fortgeschrieben}</td>
                      <td className="zahl">{l.beanstandet}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Thema>
      </Einstellungsseite>
    </div>
  );
}
